import { CallBadge } from "@/components/shared/CallBadge";
import { StoreCommunity } from "@/components/shared/StoreCommunity";
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { InternalLinks } from "@/components/shared/InternalLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getLocation } from "@/lib/api";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ChevronLeft,
  CheckCircle,
  Navigation,
  ChevronDown,
  ChevronUp,
  Info,
  ChevronRight as ChevronRightIcon,
  Star,
  Apple,
  Car,
  ExternalLink,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

function LocationFaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3" data-testid="location-faq-item">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between text-left gap-3 group"
      >
        <span className="font-medium text-navy font-body group-hover:text-teal transition-colors">
          {question}
        </span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        )}
      </button>
      {open && (
        <p className="mt-2 text-slate-600 font-body whitespace-pre-line leading-relaxed">
          {answer}
        </p>
      )}
    </div>
  );
}

function getStoreStatus(hoursStr) {
  if (!hoursStr) return null;
  const now = new Date();
  const day = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const parseTime = (t) => {
    const m = t.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!m) return null;
    let h = parseInt(m[1]);
    if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
    if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + parseInt(m[2]);
  };

  const formatTime = (mins) => {
    let h = Math.floor(mins / 60), mt = mins % 60;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${mt.toString().padStart(2, "0")} ${ampm}`;
  };

  const lines = hoursStr.split("\n").map((l) => l.trim()).filter(Boolean);
  const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const schedule = Array(7).fill(null);

  for (const line of lines) {
    const match = line.match(/^(.+?):\s*(\d{1,2}:\d{2}\s*[AP]M)\s*[-–]\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
    if (!match) continue;
    const dayPart = match[1].trim().toLowerCase();
    const open = parseTime(match[2]);
    const close = parseTime(match[3]);
    if (open === null || close === null) continue;

    const rangeParts = dayPart.split(/\s*[-–]\s*/);
    if (rangeParts.length === 2) {
      const startDay = dayMap[rangeParts[0].trim().slice(0, 3)];
      const endDay = dayMap[rangeParts[1].trim().slice(0, 3)];
      if (startDay !== undefined && endDay !== undefined) {
        for (let d = startDay; d !== (endDay + 1) % 7; d = (d + 1) % 7) {
          schedule[d] = { open, close };
        }
        schedule[endDay] = { open, close };
      }
    } else {
      const singleDay = dayMap[dayPart.slice(0, 3)];
      if (singleDay !== undefined) schedule[singleDay] = { open, close };
    }
  }

  const todaySchedule = schedule[day];
  const weekly = dayNames.map((name, i) => ({
    day: name,
    hours: schedule[i] ? `${formatTime(schedule[i].open)} - ${formatTime(schedule[i].close)}` : "Closed",
    isToday: i === day,
  }));

  if (!todaySchedule) {
    let nextOpenText = "";
    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = (day + offset) % 7;
      if (schedule[nextDay]) {
        nextOpenText = `Opens ${dayNames[nextDay]} at ${formatTime(schedule[nextDay].open)}`;
        break;
      }
    }
    return { isOpen: false, statusWord: "Closed", nextOpen: nextOpenText, weekly };
  }

  if (currentMinutes >= todaySchedule.open && currentMinutes < todaySchedule.close) {
    return { isOpen: true, statusWord: "Open", nextOpen: `until ${formatTime(todaySchedule.close)}`, weekly };
  } else {
    return { isOpen: false, statusWord: "Closed", nextOpen: `Opens at ${formatTime(todaySchedule.open)}`, weekly };
  }
}

function DetailStoreHours({ hours }) {
  const [expanded, setExpanded] = useState(false);
  const status = getStoreStatus(hours);
  if (!status) return (
    <p className="text-navy font-body text-sm whitespace-pre-line">{hours}</p>
  );

  return (
    <div data-testid="detail-store-hours">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium w-full text-left"
        data-testid="detail-hours-toggle"
      >
        <span className={status.isOpen ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>{status.statusWord}</span>
        <span className="text-slate-700">{status.nextOpen}</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-auto" />}
      </button>
      {expanded && (
        <div className="mt-3 space-y-1.5">
          {status.weekly.map((d) => (
            <div key={d.day} className={`flex justify-between text-sm ${d.isToday ? "font-bold text-teal" : "text-slate-600"}`}>
              <span className="w-28">{d.day}</span>
              <span>{d.hours}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function _haversineMi(lat1, lon1, lat2, lon2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function NearbyStores({ currentLocation }) {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    if (!currentLocation?.latitude || !currentLocation?.longitude) return;
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${apiUrl}/api/locations`)
      .then((res) => res.json())
      .then((all) => {
        if (!Array.isArray(all)) return;
        const nearest = all
          .filter(
            (l) =>
              l &&
              l.id !== currentLocation.id &&
              l.latitude &&
              l.longitude &&
              l.slug
          )
          .map((l) => ({
            ...l,
            _dist: _haversineMi(
              currentLocation.latitude,
              currentLocation.longitude,
              l.latitude,
              l.longitude
            ),
          }))
          .filter((l) => Number.isFinite(l._dist))
          .sort((a, b) => a._dist - b._dist)
          .slice(0, 3);
        setStores(nearest);
      })
      .catch(() => {});
  }, [currentLocation]);

  if (stores.length === 0) return null;

  return (
    <Card className="border-slate-200 mb-6" data-testid="nearby-stores">
      <CardContent className="p-6">
        <h2 className="font-heading font-semibold text-navy text-xl mb-4">
          More Nearby Stores
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {stores.map((s) => (
            <Link
              key={s.id}
              to={`/locations/${s.slug}`}
              className="block p-4 rounded-lg border border-slate-200 hover:border-teal hover:shadow-sm transition-all"
              data-testid={`nearby-store-${s.id}`}
            >
              <p className="font-heading font-semibold text-navy text-sm mb-1">
                {s.name}
              </p>
              <p className="text-xs text-slate-500 mb-2">
                {s.city}, {s.state}
              </p>
              <p className="text-xs text-teal font-medium">
                {s._dist.toFixed(1)} mi away
              </p>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function LocationDetailPage() {
  const { state, city, address } = useParams();
  const slug = `${state}/${city}/${address}`;
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const data = await getLocation(slug);
        setLocation(data);
      } catch (error) {
        console.error("Error fetching location:", error);
        // Set sample location if API fails
        const sampleLocations = {
          loc1: {
            id: "loc1",
            name: "Prime Media Wireless - New York",
            address: "123 Broadway",
            city: "New York",
            state: "NY",
            zip_code: "10001",
            phone: "(212) 555-0100",
            email: "newyork@primemediawireless.com",
            hours: "Monday - Friday: 9:00 AM - 7:00 PM\nSaturday: 10:00 AM - 5:00 PM\nSunday: Closed",
            description: "Our flagship location in the heart of Manhattan. We offer comprehensive Total Wireless services including device sales, plan activation, repairs, and accessories. Our expert team is ready to assist you with all your wireless needs.",
            services: ["Device Sales", "Plan Activation", "Phone Repairs", "Accessories", "Bill Pay", "Account Support"],
            image_url: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80",
          },
          loc2: {
            id: "loc2",
            name: "Prime Media Wireless - Los Angeles",
            address: "456 Sunset Blvd",
            city: "Los Angeles",
            state: "CA",
            zip_code: "90028",
            phone: "(323) 555-0200",
            email: "losangeles@primemediawireless.com",
            hours: "Monday - Friday: 9:00 AM - 8:00 PM\nSaturday - Sunday: 10:00 AM - 6:00 PM",
            description: "Serving the greater Los Angeles area with premium wireless services. Stop by for the latest devices and exclusive deals on Total Wireless plans.",
            services: ["Device Sales", "Plan Activation", "Business Solutions", "Accessories", "Trade-In Program"],
            image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=800&q=80",
          },
          loc3: {
            id: "loc3",
            name: "Prime Media Wireless - Chicago",
            address: "789 Michigan Ave",
            city: "Chicago",
            state: "IL",
            zip_code: "60611",
            phone: "(312) 555-0300",
            email: "chicago@primemediawireless.com",
            hours: "Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed",
            description: "Premier wireless services in downtown Chicago on the Magnificent Mile. Experience exceptional customer service and the full range of Total Wireless products.",
            services: ["Device Sales", "Plan Activation", "Accessories", "Account Support"],
            image_url: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800&q=80",
          },
          loc4: {
            id: "loc4",
            name: "Prime Media Wireless - Houston",
            address: "321 Main Street",
            city: "Houston",
            state: "TX",
            zip_code: "77002",
            phone: "(713) 555-0400",
            email: "houston@primemediawireless.com",
            hours: "Monday - Saturday: 9:00 AM - 7:00 PM\nSunday: 12:00 PM - 5:00 PM",
            description: "Your Total Wireless headquarters in Houston. We offer the largest selection of devices and accessories in the area, plus business solutions for companies of all sizes.",
            services: ["Device Sales", "Plan Activation", "Phone Repairs", "Accessories", "Business Solutions", "Corporate Accounts"],
            image_url: "https://images.unsplash.com/photo-1567449303078-57ad995bd329?w=800&q=80",
          },
        };
        setLocation(sampleLocations.loc1);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
    // Fetch deals
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals?active_only=true`)
      .then(res => res.json())
      .then(setDeals)
      .catch(() => {});
  }, [slug]);

  // Inject SEO meta tags and JSON-LD schema into the page head
  useEffect(() => {
    if (!location) return;
    // Set page title
    if (location.seo_title) {
      document.title = location.seo_title;
    }
    // Set meta description
    if (location.seo_description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) { metaDesc = document.createElement("meta"); metaDesc.name = "description"; document.head.appendChild(metaDesc); }
      metaDesc.content = location.seo_description;
    }
    // Set meta keywords
    if (location.seo_keywords) {
      let metaKey = document.querySelector('meta[name="keywords"]');
      if (!metaKey) { metaKey = document.createElement("meta"); metaKey.name = "keywords"; document.head.appendChild(metaKey); }
      metaKey.content = location.seo_keywords;
    }
    // Inject JSON-LD schema
    if (location.seo_schema) {
      const existingScript = document.getElementById("location-schema-jsonld");
      if (existingScript) existingScript.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "location-schema-jsonld";
      script.textContent = location.seo_schema;
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("location-schema-jsonld");
      if (el) el.remove();
    };
  }, [location]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
        </div>
      </PublicLayout>
    );
  }

  if (!location) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <MapPin className="w-16 h-16 text-slate-300 mb-4" />
          <h1 className="font-heading font-bold text-2xl text-navy mb-2">Location Not Found</h1>
          <p className="text-slate-500 mb-4 font-body">The location you're looking for doesn't exist.</p>
          <Link to="/locations">
            <Button className="bg-teal hover:bg-teal-dark text-white">
              View All Locations
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-navy py-16" data-testid="location-detail-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/locations"
            className="inline-flex items-center text-teal hover:text-teal-light mb-6 transition-colors"
            data-testid="back-to-locations"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to All Locations
          </Link>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">
            {location.name}
          </h1>
          {location.google_rating && location.google_review_count ? (
            <div className="flex items-center gap-2 mb-3" data-testid="location-rating-hero">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-5 h-5 ${
                      n <= Math.round(location.google_rating)
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-500"
                    }`}
                  />
                ))}
              </div>
              <span className="text-white font-medium">
                {Number(location.google_rating).toFixed(1)}
              </span>
              <a
                href={location.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location.address}, ${location.city}, ${location.state} ${location.zip_code}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 text-sm hover:text-teal underline"
              >
                ({location.google_review_count} Google reviews)
              </a>
            </div>
          ) : null}
          <p className="text-slate-300 font-body flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {location.address}, {location.city}, {location.state} {location.zip_code}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-slate-50" data-testid="location-detail-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2"
            >
              {/* Image */}
              {location.image_url && (
                <div className="mb-8 rounded-xl overflow-hidden">
                  <img
                    src={location.image_url}
                    alt={location.name}
                    className="w-full h-64 lg:h-80 object-cover"
                  />
                </div>
              )}

              {/* Description */}
              <Card className="border-slate-200 mb-6">
                <CardContent className="p-6">
                  <h2 className="font-heading font-semibold text-navy text-xl mb-4">About This Location</h2>
                  <p className="text-slate-600 font-body whitespace-pre-line">
                    {location.description || "Visit our location for the best Total Wireless products and services."}
                  </p>
                </CardContent>
              </Card>

              {/* Services */}
              {location.services && location.services.length > 0 && (
                <Card className="border-slate-200 mb-6">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-semibold text-navy text-xl mb-4">Services Available</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {location.services.map((service) => (
                        <div key={service} className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-teal" />
                          <span className="text-slate-700 font-body">{service}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Service Area + Driving Directions */}
              {((Array.isArray(location.service_area_zips) && location.service_area_zips.length > 0) ||
                (Array.isArray(location.nearby_cities_seo) && location.nearby_cities_seo.length > 0)) && (
                <Card className="border-slate-200 mb-6" data-testid="location-service-area">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-semibold text-navy text-xl mb-4">Areas We Serve</h2>
                    {Array.isArray(location.nearby_cities_seo) && location.nearby_cities_seo.length > 0 && (
                      <p className="text-slate-600 font-body mb-3">
                        {location.name} proudly serves customers throughout{" "}
                        <strong>
                          {[location.city, ...location.nearby_cities_seo].filter(Boolean).join(", ")}
                        </strong>{" "}
                        and the surrounding {location.state} community.
                      </p>
                    )}
                    {Array.isArray(location.service_area_zips) && location.service_area_zips.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-500 mb-2">Service area zip codes:</p>
                        <div className="flex flex-wrap gap-2">
                          {location.service_area_zips.map((z) => (
                            <span key={z} className="px-2 py-1 bg-slate-100 rounded text-sm font-mono text-navy">
                              {z}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {Array.isArray(location.nearby_cities_seo) && location.nearby_cities_seo.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Driving directions from nearby cities:</p>
                        <ul className="space-y-1">
                          {location.nearby_cities_seo.map((c) => {
                            const origin = `${c}, ${location.state}`;
                            const dest = `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`;
                            const url = `https://www.google.com/maps/dir/${encodeURIComponent(origin)}/${encodeURIComponent(dest)}`;
                            return (
                              <li key={c}>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-teal hover:underline text-sm inline-flex items-center gap-1"
                                  data-testid={`directions-from-${c}`}
                                >
                                  <Navigation className="w-3.5 h-3.5" />
                                  Directions from {c}, {location.state}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Nearby Stores */}
              <NearbyStores currentLocation={location} />

              {/* FAQs */}
              {location.faqs && location.faqs.length > 0 && (
                <Card className="border-slate-200" data-testid="location-faqs">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-semibold text-navy text-xl mb-4">
                      Frequently Asked Questions
                    </h2>
                    <div className="divide-y divide-slate-200">
                      {location.faqs.map((faq, idx) => (
                        <LocationFaqItem key={idx} question={faq.question} answer={faq.answer} />
                      ))}
                    </div>
                    {/* FAQPage JSON-LD for Google rich results */}
                    <script
                      type="application/ld+json"
                      dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "FAQPage",
                          "mainEntity": location.faqs.map((f) => ({
                            "@type": "Question",
                            "name": f.question,
                            "acceptedAnswer": { "@type": "Answer", "text": f.answer },
                          })),
                        }),
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Contact Info Card */}
              <Card className="border-slate-200 mb-6 sticky top-24">
                <CardContent className="p-6">
                  <h2 className="font-heading font-semibold text-navy text-lg mb-4">Contact Information</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-teal" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 font-body">Address</p>
                        <p className="text-navy font-medium font-body">
                          {location.address}<br />
                          {location.city}, {location.state} {location.zip_code}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-teal" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 font-body mb-1">Phone</p>
                        <CallBadge phone={location.phone} size="lg" testId="location-call-badge" />
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-teal" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 font-body">Email</p>
                        {location.email ? (
                          <a
                            href={`mailto:${location.email}`}
                            className="text-navy font-medium font-body hover:text-teal transition-colors break-all"
                          >
                            {location.email}
                          </a>
                        ) : <p className="text-slate-500 font-body" data-testid="location-email-coming-soon">Coming soon</p>}
                      </div>
                    </div>

                    {location.hours && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-teal" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-500 font-body mb-1">Hours</p>
                          <DetailStoreHours hours={location.hours} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200">
                    {/* Mini Map */}
                    {location.latitude && location.longitude && (
                      <div className="mb-4 rounded-lg overflow-hidden border border-slate-200" style={{ height: "180px" }}>
                        <MapContainer
                          center={[location.latitude, location.longitude]}
                          zoom={14}
                          style={{ height: "100%", width: "100%" }}
                          scrollWheelZoom={false}
                          dragging={false}
                          zoomControl={false}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker position={[location.latitude, location.longitude]}>
                            <Popup>{location.name}</Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                    <Button
                      className="w-full bg-teal hover:bg-teal-dark text-white"
                      onClick={() => {
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                            `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`
                          )}`,
                          "_blank"
                        );
                      }}
                      data-testid="get-directions-btn"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Google Maps Directions
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full mt-2"
                      onClick={() => {
                        window.open(
                          `https://maps.apple.com/?daddr=${encodeURIComponent(
                            `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`
                          )}`,
                          "_blank"
                        );
                      }}
                      data-testid="apple-maps-btn"
                    >
                      <Apple className="w-4 h-4 mr-2" />
                      Apple Maps
                    </Button>
                    {location.latitude && location.longitude && (
                      <Button
                        variant="outline"
                        className="w-full mt-2"
                        onClick={() => {
                          const fullAddr = `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`;
                          window.open(
                            `https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=${location.latitude}&dropoff[longitude]=${location.longitude}&dropoff[formatted_address]=${encodeURIComponent(fullAddr)}`,
                            "_blank"
                          );
                        }}
                        data-testid="uber-btn"
                      >
                        <Car className="w-4 h-4 mr-2" />
                        Ride with Uber
                      </Button>
                    )}
                    {location.phone && <a href={`tel:${location.phone.replace(/[^\d+]/g, "")}`}>
                      <Button variant="outline" className="w-full mt-2" data-testid="call-location-btn">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                    </a>}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Explore In-Store Deals */}
      {deals.length > 0 && (
        <section className="py-12 bg-navy" data-testid="explore-deals-section">
          <div className="mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex items-center justify-center gap-2 mb-8 relative">
              <h2 className="font-heading font-bold text-2xl lg:text-3xl text-white">Explore in-store deals</h2>
              <button
                onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="deals-info-btn"
              >
                <Info className="w-5 h-5" />
              </button>
              {showInfoTooltip && (
                <div className="absolute top-full mt-2 bg-white text-slate-700 text-sm px-4 py-2 rounded-lg shadow-lg z-10" data-testid="deals-info-tooltip">
                  Deals subject to in-store availability
                </div>
              )}
            </div>
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {deals.map((deal, idx) => (
                  <motion.div
                    key={deal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    className="overflow-visible"
                  >
                    <div className="bg-gradient-to-b from-teal/10 to-teal/5 rounded-xl h-full flex flex-col border border-white/10" data-testid={`deal-card-${deal.id}`}>
                      <div className="p-5 flex-1">
                        <h3 className="font-heading font-bold text-white text-sm leading-tight mb-2">{deal.title}</h3>
                        {deal.description && (
                          <p className="text-white/70 text-xs leading-relaxed" style={{ overflowWrap: "anywhere" }}>{deal.description}</p>
                        )}
                      </div>
                      {deal.image_url && (
                        <div className="px-2 pb-2">
                          <img
                            src={deal.image_url}
                            alt={deal.title}
                            className="w-full h-40 object-contain rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <StoreCommunity location={location} />
      <InternalLinks exclude="/locations" />
    </PublicLayout>
  );
}
